import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotesIcon from '@mui/icons-material/Notes';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAddOwnerNotesMutation } from '../../../../../app/Slices/SevenDayApiSlice';
import { CustomTextField } from '../../../../../layout/styledcomponents/CustomTextField';
import { theme } from '../../../../../shared/theme';
import toast from '../../../../shared/Toast';

type Props = {
    reservation: any;
    expanded?: boolean;
    onToggle?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
};

export default function ReservationNotesAccordion({ reservation }: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const fullScreen = useMediaQuery('(max-width: 600px)');
    const [open, setOpen] = useState(false);
    const [addOwnerNotes] = useAddOwnerNotesMutation();

    const initialNotes = useMemo(
        () => reservation?.ownerNotes || '',
        [reservation?.ownerNotes],
    );
    const [ownerNotes, setOwnerNotes] = useState(initialNotes);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setOwnerNotes(initialNotes);
    }, [initialNotes]);

    const isDirty = ownerNotes.trim() !== (initialNotes || '').trim();
    const isEmpty = ownerNotes.trim().length === 0;

    const handleOwnerNotes = (event: any) => {
        setOwnerNotes(event?.target?.value ?? '');
    };

    const saveOwnerNotes = async () => {
        if (!reservation?.id) return;
        if (!isDirty) return;

        try {
            setSaving(true);
            await addOwnerNotes({
                reservationId: reservation.id,
                ownerNotes,
            }).unwrap();
            toast.success('Notes saved');
        } catch (error) {
            toast.error('Failed to save your notes');
        } finally {
            setSaving(false);
        }
    };

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        setOpen(false);
        setOwnerNotes(initialNotes);
    };

    const handleDiscard = () => setOwnerNotes(initialNotes);

    const companyName = reservation?.owner?.name;

    return (
        <>
            <Box
                onClick={handleOpen}
                role='button'
                tabIndex={0}
                sx={{
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    backgroundColor: theme.palette.custom.cardActionBackground,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    '&:hover': {
                        borderColor: theme.palette.info.main,
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.neutral.main,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        }}
                    >
                        <NotesIcon fontSize='small' sx={{ opacity: 0.75 }} />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            variant='body3'
                            sx={{
                                fontWeight: 800,
                                letterSpacing: 0.2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                            }}
                        >
                            {companyName} notes
                            <VisibilityOffIcon
                                sx={{ opacity: 0.55 }}
                                fontSize='small'
                            />
                        </Typography>

                        <Typography
                            variant='body3'
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 12,
                                lineHeight: 1.2,
                            }}
                        >
                            Private notes (host & admins only)
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    variant='body3'
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Open
                </Typography>
            </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={fullScreen}
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: fullScreen ? 0 : 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        display: 'flex',
                        flexDirection: 'column',
                        height: fullScreen ? '100dvh' : 'auto',
                        backgroundColor: theme.palette.neutral.main,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        position: fullScreen ? 'sticky' : 'relative',
                        top: 0,
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            BHAG management notes{' '}
                            <VisibilityOffIcon
                                sx={{ opacity: 0.55 }}
                                fontSize='small'
                            />{' '}
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography
                                variant='body3'
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: 12,
                                }}
                            >
                                Notes are private and won’t be shown to guests
                            </Typography>
                        </Box>
                    </Box>

                    <IconButton onClick={handleClose} size='small'>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                        overflow: 'auto',
                    }}
                >
                    <CustomTextField
                        placeholder='Add internal notes for this reservation…'
                        fullWidth
                        color='dark'
                        variant='outlined'
                        value={ownerNotes}
                        onChange={handleOwnerNotes}
                        multiline
                        minRows={fullScreen ? 10 : 8}
                        sx={{
                            backgroundColor: theme.palette.neutral.main,
                            borderRadius: 2,
                            flex: 1,
                            '& .MuiInputBase-root': {
                                height: fullScreen ? '100%' : 'auto',
                                alignItems: 'flex-start',
                            },
                            '& textarea': {
                                height: fullScreen ? '100% !important' : 'auto',
                            },
                        }}
                    />

                    <Typography
                        variant='body3'
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <span>
                            {isDirty ? 'Unsaved changes' : 'Up to date'}
                        </span>
                        <span>{(ownerNotes || '').length} chars</span>
                    </Typography>
                </DialogContent>

                <DialogActions
                    sx={{
                        position: fullScreen ? 'sticky' : 'relative',
                        bottom: 0,
                        zIndex: 2,
                    }}
                >
                    {fullScreen ? (
                        <Stack
                            direction='column'
                            spacing={1}
                            sx={{ width: '100%' }}
                        >
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={!isDirty || isEmpty || saving}
                                onClick={saveOwnerNotes}
                                sx={{
                                    borderRadius: 2,
                                    minHeight: 44,
                                    width: '100%',
                                }}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </Button>

                            <Stack
                                direction='row'
                                spacing={1}
                                sx={{ width: '100%' }}
                            >
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    disabled={!isDirty || saving}
                                    onClick={handleDiscard}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 44,
                                        flex: 1,
                                    }}
                                >
                                    Discard
                                </Button>

                                <Button
                                    variant='outlined'
                                    color='primary'
                                    disabled={saving}
                                    onClick={handleClose}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 44,
                                        flex: 1,
                                    }}
                                >
                                    Close
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                variant='outlined'
                                color='primary'
                                disabled={!isDirty || saving}
                                onClick={handleDiscard}
                                sx={{ borderRadius: 2, minHeight: 40 }}
                            >
                                Discard
                            </Button>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    disabled={saving}
                                    onClick={handleClose}
                                    sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                    Close
                                </Button>

                                <Button
                                    variant='contained'
                                    color='primary'
                                    disabled={!isDirty || isEmpty || saving}
                                    onClick={saveOwnerNotes}
                                    sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                    {saving ? 'Saving…' : 'Save'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
